import type { ApiInterface } from '../../utilities/ApiInterface';
import { Api } from '@automapa/svelte-automapa-template';
import { z } from 'zod';
import {
    AccountCreateResponseSchema,
    type AccountCreateResponseType,
    AccountRequestSchema,
    type AccountRequestType,
    AccountResponseSchema,
    type AccountResponseType,
    SetUserManagerRequestSchema,
    type SetUserManagerRequestType,
    SetUserManagerResponseSchema,
    type SetUserManagerResponseType,
} from '../Settings/SettingsApi';
import {
    CustomerSettingsReadonlySchema,
    type CustomerSettingsReadonlyType,
} from '../Settings/settings';

export class AdminApi implements ApiInterface {
    private static _endpoint: string = 'admin';

    public static async createAccount(
        api: Api,
        name: string,
        email: string,
        expiration_date: string,
        settings_readonly: CustomerSettingsReadonlyType
    ): Promise<AccountCreateResponseType> {
        return api.getData<AccountRequestType, AccountCreateResponseType>(
            'post',
            `/${this._endpoint}/create_account`,
            { name, email, expiration_date, is_expired: false, settings_readonly },
            AccountRequestSchema,
            AccountCreateResponseSchema
        );
    }

    public static async getAccounts(api: Api): Promise<AccountResponseType[]> {
        return api.getData<any, AccountResponseType[]>(
            'post',
            `/${this._endpoint}/get_accounts`,
            {},
            z.any(),
            z.array(AccountResponseSchema)
        );
    }

    public static async updateExpirationDate(
        api: Api,
        customer_id: number,
        expiration_date: string
    ): Promise<any> {
        return api.getData<{ customer_id: number; expiration_date: string }, any>(
            'post',
            `/${this._endpoint}/update_expiration_date`,
            { customer_id, expiration_date },
            z.object({
                customer_id: z.number(),
                expiration_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/i),
            }),
            z.any()
        );
    }

    /**
     * Nadanie lub odebranie roli user_manager (US-018). Nadanie wymusza po stronie backendu
     * can_read i can_write, więc odpowiedź jest jedynym źródłem prawdy o stanie konta po zmianie -
     * front jej nie zgaduje.
     */
    public static async setUserManager(
        api: Api,
        webuser_id: number,
        is_user_manager: boolean
    ): Promise<SetUserManagerResponseType> {
        return api.getData<SetUserManagerRequestType, SetUserManagerResponseType>(
            'post',
            `/${this._endpoint}/set_user_manager`,
            { webuser_id, is_user_manager },
            SetUserManagerRequestSchema,
            SetUserManagerResponseSchema
        );
    }

    public static async updateSettingsReadonly(
        api: Api,
        customer_id: number,
        settings_readonly: CustomerSettingsReadonlyType
    ): Promise<CustomerSettingsReadonlyType> {
        return api.getData<
            { customer_id: number; settings_readonly: CustomerSettingsReadonlyType },
            any
        >(
            'post',
            `/${this._endpoint}/update_settings_readonly`,
            { customer_id, settings_readonly },
            z.object({
                customer_id: z.number(),
                settings_readonly: CustomerSettingsReadonlySchema,
            }),
            CustomerSettingsReadonlySchema
        );
    }
}
